import { HotelInfoModel } from '@/__generated';
import React from 'react';
import { HotelServicesAndConveniences } from '../type';
import { UncontrolledTooltip } from 'reactstrap';

const countryIcons = {
  chineseFlag: require('@/assets/icons/flags/tn_ch-flag.gif'),
  koreanFlag: require('@/assets/icons/flags/tn_ks-flag.gif'),
  englishFlag: require('@/assets/icons/flags/tn_uk-flag.png'),
};
const wheelchairIcon = require('@/assets/icons/facilities/wheelchair.png');
const elevatorIcon = require('@/assets/icons/facilities/elevator.png');

type Props = { hotelInfo: HotelInfoModel };

export default function HotelFacilities({ hotelInfo }: Props) {
  const services = React.useMemo(() => {
    return hotelInfo.furniture?.map(f => {
      let icon = HotelServicesAndConveniences[f]?.icon;
      if (icon) {
        return {
          name: f,
          icon: icon,
        };
      }
      return {
        name: f,
        icon: 'ficon-confirmation-instant',
      };
    });
  }, [hotelInfo]);
  const hasChildPool = React.useMemo(() => {
    return hotelInfo.hotelPools?.some(x => x.hasChildPool) || false;
  }, [hotelInfo]);

  const bfBuildingEntrance = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfBuildingEntrance);
  }, [hotelInfo]);

  const bfCorridor = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfCorridor);
  }, [hotelInfo]);

  const bfWheelChairElevator = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfWheelChairElevator);
  }, [hotelInfo]);

  const bfToilet = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfToilet);
  }, [hotelInfo]);

  const bfWheelChairControllerElevator = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfWheelChairControllerElevator);
  }, [hotelInfo]);
  const bfHandrailElevator = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfHandrailElevator);
  }, [hotelInfo]);
  const bfWheelChairToilet = React.useMemo(() => {
    return hotelInfo.hotelBuildings?.some(x => x.bfWheelChairToilet);
  }, [hotelInfo]);

  return (
    <div className="col-12 pt-20 pb-20" id="facilities">
      <h3 className="text-22 fw-500 pt-30 border-top-light">Amenities and facilities</h3>
      <div className="row y-gap-10 pt-20">
        {hotelInfo.englishCommunication || hotelInfo.koreanCommunication || hotelInfo.chineseCommunication ? (
          <div className="col-md-3">
            <div className="text-16 fw-500 pb-10">Languages spoken</div>
            {hotelInfo.englishCommunication ? (
              <div className="d-flex x-gap-15 items-center">
                <img src={countryIcons.englishFlag} alt="" className="h-20 w-30" />
                <div className="text-15">English</div>
              </div>
            ) : null}
            {hotelInfo.koreanCommunication ? (
              <div className="d-flex x-gap-15 items-center">
                <img src={countryIcons.koreanFlag} alt="Korean" className="h-20" />
                <div className="text-15">Korean</div>
              </div>
            ) : null}
            {hotelInfo.chineseCommunication ? (
              <div className="d-flex x-gap-15 items-center">
                <img src={countryIcons.chineseFlag} alt="Chinese" className="h-20" />
                <div className="text-15">Chinese </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* child */}
        {hotelInfo.hasBabyBed || hotelInfo.hasBabyStrollers || hotelInfo.hasChildMeal || hasChildPool ? (
          <div className="col-md-3">
            <div className="text-16 fw-500 pb-10">For kids</div>
            {hotelInfo.hasBabyBed ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <i className={`ficon-baby-cot ficon text-16`}></i>
                <div className="text-15">{`Crib [${hotelInfo.babyBedFee ? 'With Charge.' : 'Free of Charge'}]`}</div>
              </div>
            ) : null}
            {hotelInfo.hasBabyStrollers ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <i className={`ficon-babysitting ficon text-16`}></i>
                <div className="text-15">{`Stroller [${hotelInfo.babyStrollersFee ? 'With Charge.' : 'Free of Charge'}]`}</div>
              </div>
            ) : null}
            {hotelInfo.hasChildMeal ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <i className={`ficon-regular-child-meal-b ficon text-16`}></i>
                <div className="text-15">{`Tableware [${hotelInfo.childMealFee ? 'With Charge.' : 'Free of Charge'}]`}</div>
              </div>
            ) : null}
            {hasChildPool ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <i className={`ficon-pool-kids ficon text-16`}></i>
                <div className="text-15">Swimming pool [kids]</div>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* service */}
        {services?.length ? (
          <div className="col-md-3">
            <div className="text-16 fw-500 pb-10">Services and conveniences</div>
            {services?.map(s => {
              return (
                <div className="d-flex x-gap-15 y-gap-15 items-center" key={s.name}>
                  <i className={`${s.icon} ficon text-16`}></i>
                  <div className="text-15">{s.name}</div>
                </div>
              );
            })}
          </div>
        ) : null}
        {bfBuildingEntrance ||
        bfCorridor ||
        bfWheelChairElevator ||
        bfWheelChairControllerElevator ||
        bfHandrailElevator ||
        bfToilet ||
        bfWheelChairToilet ? (
          <div className="col-md-3">
            <div className="text-16 fw-500 pb-10">Disability Accessibility</div>
            {bfBuildingEntrance ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <div style={{ padding: 4 }}>
                  <img src={wheelchairIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15 text-blue-1" id="Barrier-free-entrance">
                  Barrier-free entrance
                </div>
                <UncontrolledTooltip target="Barrier-free-entrance">
                  Passing by a wheelchair smoothly(no steps, enough width and hard to slip)
                </UncontrolledTooltip>
              </div>
            ) : null}
            {bfCorridor ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <div style={{ padding: 4 }}>
                  <img src={wheelchairIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15">Corridor</div>
              </div>
            ) : null}
            {bfToilet ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <div style={{ padding: 4 }}>
                  <img src={wheelchairIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15 ">Barrier-free toilet </div>
              </div>
            ) : null}
            {bfWheelChairToilet ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <div style={{ padding: 4 }}>
                  <img src={wheelchairIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15 ">Toilet can be used with wheelchair </div>
              </div>
            ) : null}
            {bfWheelChairElevator ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <div style={{ padding: 4 }}>
                  <img src={elevatorIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15">[Elevator] The wheelchair can pass smoothly</div>
              </div>
            ) : null}
            {bfWheelChairControllerElevator ? (
              <div className="d-flex x-gap-15 y-gap-15 items-center">
                <div style={{ padding: 4 }}>
                  <img src={elevatorIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15">[Elevator] Controller that can be operated by the height of the wheelchair</div>
              </div>
            ) : null}
            {bfHandrailElevator ? (
              <div className="d-flex x-gap-15 y-gap-15 items-start">
                <div style={{ padding: 4 }}>
                  <img src={elevatorIcon} style={{ width: 22, height: 22 }} />
                </div>
                <div className="text-15">Elevator with handrail</div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
